import * as T from "three";
import { numbers } from "./bsp.js";
import { Component } from "./live-codec.js";

// Recover brush windings from collision planes, including clip/trigger faces
// which VBSP deliberately omits from its render face lump.
export function brushTriangles(planes) {
    const result = [];
    for (let side = 0; side < planes.length; side++) {
        const { normal: n, distance: d } = planes[side];
        const normal = new T.Vector3(...n), center = normal.clone().multiplyScalar(d);
        const right = new T.Vector3(...(Math.abs(n[2]) > .9 ? [0, 1, 0] : [0, 0, 1])).cross(normal).normalize();
        const up = normal.clone().cross(right);
        let polygon = [[-1, -1], [1, -1], [1, 1], [-1, 1]].map(([x, y]) =>
            center.clone().addScaledVector(right, x * 131072).addScaledVector(up, y * 131072));
        for (let other = 0; other < planes.length && polygon.length; other++) {
            if (other === side) continue;
            const { normal, distance } = planes[other], clipped = [];
            const dot = point => normal[0] * point.x + normal[1] * point.y + normal[2] * point.z - distance;
            for (let i = 0; i < polygon.length; i++) {
                const a = polygon[i], b = polygon[(i + 1) % polygon.length], da = dot(a), db = dot(b);
                if (da <= .01) clipped.push(a);
                if ((da > .01) !== (db > .01)) clipped.push(a.clone().lerp(b, da / (da - db)));
            }
            polygon = clipped;
        }
        for (let i = 1; i + 1 < polygon.length; i++) result.push(...polygon[0].toArray(), ...polygon[i].toArray(), ...polygon[i + 1].toArray());
    }
    return new Float32Array(result);
}

export function modelBrushes(visibility, headnode) {
    const stack = [headnode], visited = new Set(), brushes = new Set();
    while (stack.length) {
        const node = stack.pop();
        if (visited.has(node)) continue;
        visited.add(node);
        if (visited.size > visibility.nodes.bytes.length / 32 + visibility.leaves.bytes.length / visibility.leafStride) break;
        if (node >= 0) {
            stack.push(visibility.nodes.i32(node * 32 + 4), visibility.nodes.i32(node * 32 + 8));
        } else {
            const leaf = (-node - 1) * visibility.leafStride;
            const first = visibility.leaves.u16(leaf + 24), count = visibility.leaves.u16(leaf + 26);
            for (let i = 0; i < count; i++) brushes.add(visibility.leafBrushes.u16((first + i) * 2));
        }
    }
    return brushes;
}

export class DebugRendering {
    constructor(view, data) {
        this.view = view; this.data = data; this.resources = new Set(); this.options = {};
        this.root = new T.Group(); this.root.userData.debug = true; view.scene.add(this.root);
        this.groups = new Map(); this.wireframes = new Map(); this.bounds = new Map();
    }
    own(resource) { this.resources.add(resource); return resource; }
    buildBrushes(kind) {
        const group = new T.Group(), v = this.view.visibility, selected = new Map();
        if (kind === "clips") for (let id = 0; id < v.brushes.bytes.length / 12; id++) {
            if (v.brushes.i32(id * 12 + 8) & (0x10000 | 0x20000)) selected.set(id, null);
        }
        if (kind === "triggers") for (const entity of this.data.entities) {
            if (!entity.classname?.startsWith("trigger_") || !entity.model?.startsWith("*")) continue;
            const model = this.data.models[Number(entity.model.slice(1))];
            if (model && v.nodes.bytes.length && v.leafStride) for (const id of modelBrushes(v, model.headnode)) selected.set(id, entity);
        }
        const material = this.own(new T.MeshBasicMaterial({ color: kind === "clips" ? 0xffcc33 : 0x55ffaa,
            transparent: true, opacity: .22, depthWrite: false, side: T.DoubleSide }));
        const edgesMaterial = this.own(new T.LineBasicMaterial({ color: kind === "clips" ? 0xffcc33 : 0x55ffaa }));
        let vertices = 0;
        for (const [id, entity] of selected) {
            const first = v.brushes.i32(id * 12), count = v.brushes.i32(id * 12 + 4);
            if (count < 4 || count > 128) continue;
            const planes = Array.from({ length: count }, (_, i) => {
                const offset = v.brushSides.u16((first + i) * 8) * 20;
                return { normal: v.planes.vec(offset), distance: v.planes.f32(offset + 12) };
            });
            const positions = brushTriangles(planes); vertices += positions.length / 3;
            if (vertices > 1000000) { this.view.diagnostic?.("Debug brush geometry budget reached."); break; }
            const geometry = this.own(new T.BufferGeometry()); geometry.setAttribute("position", new T.BufferAttribute(positions, 3));
            const mesh = new T.Mesh(geometry, material);
            mesh.add(new T.LineSegments(this.own(new T.EdgesGeometry(geometry)), edgesMaterial));
            if (entity) { mesh.position.fromArray(numbers(entity.origin)); this.view.angles(mesh, numbers(entity.angles)); }
            group.add(mesh);
        }
        return group;
    }
    buildEntities() {
        const group = new T.Group(), geometry = this.own(new T.OctahedronGeometry(8));
        const material = this.own(new T.MeshBasicMaterial({ color: 0x77ccff, wireframe: true }));
        for (const entity of this.data.entities) {
            if (entity.model || !entity.origin || entity.classname === "worldspawn") continue;
            const marker = new T.Mesh(geometry, material); marker.position.fromArray(numbers(entity.origin));
            marker.userData.classname = entity.classname; marker.userData.targetname = entity.targetname;
            group.add(marker);
        }
        return group;
    }
    set(options) {
        this.options = { ...this.options, ...options };
        for (const kind of ["triggers", "clips", "entities"]) {
            if (this.options[kind] && !this.groups.has(kind)) {
                try {
                    const group = kind === "entities" ? this.buildEntities() : this.buildBrushes(kind);
                    this.groups.set(kind, group); this.root.add(group);
                } catch (error) { this.view.diagnostic?.(`Debug ${kind}: ${error.message}`); }
            }
            if (this.groups.has(kind)) this.groups.get(kind).visible = !!this.options[kind];
        }
        this.update();
    }
    update() {
        const wantedMaterials = new Set(), wantedBounds = new Set();
        if (this.options.wireframe || this.wireframes.size) for (const scene of [this.view.scene, this.view.skyScene]) scene?.traverse(object => {
            if (!object.isMesh || object.userData.debug || this.isDebug(object)) return;
            for (const material of Array.isArray(object.material) ? object.material : [object.material]) {
                wantedMaterials.add(material);
                if (!this.wireframes.has(material)) this.wireframes.set(material, material.wireframe);
                material.wireframe = !!this.options.wireframe || this.wireframes.get(material);
            }
        });
        for (const [material, original] of this.wireframes) if (!wantedMaterials.has(material) || !this.options.wireframe) {
            material.wireframe = original; this.wireframes.delete(material);
        }
        if (this.options.bounds) for (const entity of this.view.liveScene?.entities.values() ?? []) {
            wantedBounds.add(entity.key);
            let helper = this.bounds.get(entity.key);
            if (!helper) { helper = new T.Box3Helper(new T.Box3(), 0xff77dd); this.bounds.set(entity.key, helper); this.root.add(helper); }
            helper.box.setFromObject(entity.object); helper.visible = entity.object.visible && !helper.box.isEmpty();
        }
        for (const [key, helper] of this.bounds) if (!wantedBounds.has(key)) { helper.removeFromParent(); helper.dispose(); this.bounds.delete(key); }
        // Point-only streamed entities include logical/control-point records.
        if (this.options.entities) {
            const group = this.groups.get("entities");
            if (group) {
                this.liveMarkers ??= new Map(); const wanted = new Set();
                for (const entity of this.view.liveScene?.entities.values() ?? []) if (entity.state.components[Component.Appearance]?.category === 9) {
                    wanted.add(entity.key); let marker = this.liveMarkers.get(entity.key);
                    if (!marker) {
                        const sample = group.children[0];
                        const geometry = sample?.geometry ?? this.own(new T.OctahedronGeometry(8));
                        const material = sample?.material ?? this.own(new T.MeshBasicMaterial({ color: 0x77ccff, wireframe: true }));
                        marker = new T.Mesh(geometry, material); group.add(marker); this.liveMarkers.set(entity.key, marker);
                    }
                    marker.position.copy(entity.object.getWorldPosition(new T.Vector3()));
                }
                for (const [key, marker] of this.liveMarkers) if (!wanted.has(key)) { marker.removeFromParent(); this.liveMarkers.delete(key); }
            }
        }
    }
    isDebug(object) { for (let parent = object; parent; parent = parent.parent) if (parent === this.root) return true; return false; }
    dispose() {
        for (const [material, original] of this.wireframes) material.wireframe = original;
        for (const helper of this.bounds.values()) helper.dispose();
        for (const resource of this.resources) resource.dispose();
        this.root.removeFromParent();
    }
}
